import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { formatDateTime } from "../../lib/format";

function Preview({ d, p }) {
    return (
        <div className="rounded-md border border-indigo-100 bg-indigo-50/50 p-3" data-testid={`${p}-preview`}>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Bell notice preview · opens {d.link}</div>
            <div className="text-sm font-semibold text-slate-900">{d.title}</div>
            <div className="text-xs text-slate-700 mt-0.5">{d.body}</div>
        </div>
    );
}

function Audience({ d, p, who }) {
    return (
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid={`${p}-audience`}>
            Will be delivered to <b data-testid={`${p}-count`}>{d.recipients}</b> {who}.
            {d.without_login.count > 0 && (
                <div className="mt-1 text-xs text-amber-800" data-testid={`${p}-no-login`}>
                    {d.without_login.count} active employee(s) have no login and will not receive it, e.g. {d.without_login.sample.join(", ")}{d.without_login.count > d.without_login.sample.length ? "…" : ""}
                </div>
            )}
        </div>
    );
}

function NoticeBlock({ path, p, label, who, onSent }) {
    const [d, setD] = useState(null);
    const [agree, setAgree] = useState(false);
    const [busy, setBusy] = useState(false);
    const load = () => api.get(path).then((r) => setD(r.data)).catch((e) => toast.error(formatApiError(e, "Load failed")));
    useEffect(() => { load(); }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
    if (!d) return null;
    const rec = d.record;
    const send = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`${path}/send`, { confirm: "SEND" });
            toast.success(`${label} sent to ${data.notified} employee(s).`);
            onSent?.();
        } catch (e) { toast.error(formatApiError(e, "Send failed")); }
        setBusy(false);
        setAgree(false);
        load();
    };
    return (
        <div className="space-y-3 text-sm" data-testid={`${p}-block`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
            <Preview d={d} p={p} />
            {d.sent ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-900" data-testid={`${p}-sent`}>
                    Sent on <b>{formatDateTime(rec.sent_at)}</b> by <b>{rec.sent_by}</b> to <b>{rec.notified}</b> employee(s). It cannot be sent again.
                </div>
            ) : (
                <>
                    <Audience d={d} p={p} who={who} />
                    {rec?.status === "sending" && <div className="text-xs text-amber-800" data-testid={`${p}-in-progress`}>A send started at {formatDateTime(rec.started_at)} is in progress.</div>}
                    <label className="flex items-start gap-2 text-xs text-slate-700">
                        <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} data-testid={`${p}-confirm`} />
                        I understand this sends the notice once to {d.recipients} employee(s) and cannot be undone or repeated.
                    </label>
                    <div className="flex justify-end">
                        <Button disabled={!agree || busy || rec?.status === "sending"} onClick={send} data-testid={`${p}-send`}>
                            {busy ? "Sending…" : `Send to ${d.recipients} employee(s)`}
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
}

export function LaunchNoticeDialog({ open, onClose }) {
    const [launchKey, setLaunchKey] = useState(0);
    const [launchSent, setLaunchSent] = useState(false);
    useEffect(() => {
        if (open) api.get("/attendance/launch-notice/follow-up").then((r) => setLaunchSent(r.data.launch_sent)).catch(() => setLaunchSent(false));
    }, [open, launchKey]);
    if (!open) return null;
    return (
        <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="launch-notice-dialog">
                <DialogHeader>
                    <DialogTitle>Attendance Regularisation notices</DialogTitle>
                    <DialogDescription>One-time notices in employees' notification bells. No email is sent.</DialogDescription>
                </DialogHeader>
                <NoticeBlock key={`l${launchKey}`} path="/attendance/launch-notice" p="launch-notice" label="Launch notice" who="active employee(s) with a login" onSent={() => setLaunchKey((k) => k + 1)} />
                {launchSent && (
                    <div className="border-t border-slate-200 pt-4">
                        <NoticeBlock path="/attendance/launch-notice/follow-up" p="followup-notice" label="Follow-up: User Guide notice" who="employee(s) who received the launch notice" />
                    </div>
                )}
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="launch-notice-close">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
